import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Tab, TabList } from "@fluentui/react-components";
import { AppDispatch, RootState } from "../../../redux/store";
import { fetchDocuments, resetDocuments, setCurrentPath } from "../../../redux/Document/documentSlice";
import DocumentActions from "./AssystDrive/DocumentActions";
import DocumentList from "./AssystDrive/DocumentList";
import FileLinkPanel from "./FileLink/FileLinkPanel";
import Search from "../../../components/Search";
import { DocumentLoader } from "../Documents/docLoader";
import "./style.css";

interface FileItem {
  type: "file";
  name: string;
  path: string;
  lastModified: string;
  metadata: any;
}

interface FolderItem {
  type: "folder";
  name: string;
  path: string;
  lastModified: string;
  children: (FolderItem | FileItem)[];
}

type Item = FolderItem | FileItem;

function buildFileStructure(paths: any[]): Item[] {
  const root: any[] = [];
  const pathMap = new Map<string, any>();

  paths.forEach(({ path, metadata, type, lastModified }) => {
    const cleanPath = path.endsWith("/") ? path.slice(0, -1) : path;
    const parts = cleanPath.split("/").filter(Boolean);
    let currentPathStr = "";
    let parent: any = null;

    parts.forEach((part: string, index: number) => {
      currentPathStr = parts.slice(0, index + 1).join("/");
      const isFolder = index < parts.length - 1 || type === "folder";

      if (!pathMap.has(currentPathStr)) {
        const node: any = {
          name: part,
          type: isFolder ? "folder" : "file",
          path: currentPathStr,
          lastModified: isFolder ? "" : lastModified,
          children: [],
          metadata: isFolder ? {} : metadata,
        };
        pathMap.set(currentPathStr, node);
        if (parent) parent.children.push(node);
        else root.push(node);
      } else if (!isFolder) {
        const existing = pathMap.get(currentPathStr);
        if (existing) existing.lastModified = lastModified;
      }
      parent = pathMap.get(currentPathStr);
    });
  });

  const sortStructure = (items: any[]) => {
    items.sort((a: any, b: any) =>
      a.type === b.type
        ? a.name.localeCompare(b.name)
        : a.type === "folder"
          ? -1
          : 1
    );
    items.forEach((item: any) => {
      if (item.type === "folder") sortStructure(item.children);
    });
  };
  sortStructure(root);
  return root;
}

function getCurrentFolder(path: string[], structure: Item[]): Item[] {
  let current: Item[] = structure;
  for (const segment of path) {
    const found = (current as FolderItem[]).find(
      (item) => item.name === segment && item.type === "folder"
    );
    if (found && found.type === "folder") {
      current = found.children;
    } else {
      break;
    }
  }
  return current;
}

type TabValue = "assyst-drive" | "file-link";

// ── Shared drive panel — each tab mounts its own instance ─────────────────────
interface DrivePanelProps {
  activeCustomerId: string;
  notify: any;
  clientId: string;
  splatPath: string;
  tab: TabValue;
}

function DrivePanel({ activeCustomerId, notify, clientId, splatPath, tab }: DrivePanelProps) {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { fileList, loading, basePath } = useSelector(
    (state: RootState) => state.documents
  );
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [fileStructure, setFileStructure] = useState<Item[]>([]);

  const currentPath = splatPath ? splatPath.split("/").filter(Boolean) : [];

  useEffect(() => {
    dispatch(setCurrentPath(currentPath));
  }, [splatPath]);

  useEffect(() => {
    dispatch(fetchDocuments(activeCustomerId));
    return () => {
      dispatch(resetDocuments());
    };
  }, [activeCustomerId]);

  useEffect(() => {
    if (search) {
      const searchLower = search.toLowerCase();
      const filtered = fileList.filter((item) => {
        const nameMatch = item.name.toLowerCase().includes(searchLower);
        // const descMatch =
        //   item.type === "file"
        return nameMatch;
      });
      setSearchResults(filtered);
      setFileStructure([]);
    } else {
      setFileStructure(buildFileStructure(fileList));
      setSearchResults([]);
    }
  }, [fileList, search]);

  const navigateTo = (path: string[]) => {
    setSearch("");
    const folderSuffix = path.length > 0 ? `/${path.join("/")}` : "";
    navigate(`/clients/${clientId}/documents/${tab}${folderSuffix}`);
  };

  const refreshDocuments = () => {
    dispatch(fetchDocuments(activeCustomerId));
  };

  const currentFolder = getCurrentFolder(currentPath, fileStructure);

  return (
    <>
      <div className="doc-mgmt-toolbar">
        <Search setSearch={setSearch} search={search} />
        <DocumentActions
          currentPath={currentPath}
          basePath={basePath}
          activeCustomerId={activeCustomerId}
          notify={notify}
          onRefresh={refreshDocuments}
        />
      </div>
      {loading ? (
        <DocumentLoader />
      ) : fileList.length > 0 ? (
        <DocumentList
          structure={search ? [] : currentFolder}
          searchResults={searchResults}
          search={search}
          navigateTo={navigateTo}
          currentPath={currentPath}
          activeCustomerId={activeCustomerId}
        />
      ) : (
        <div className="doc-mgmt-empty">
          There are no active records — click Add Folder or Add Document to
          create a new file
        </div>
      )}
    </>
  );
}

// ── Main DocumentManagement with tabs ─────────────────────────────────────────
const TABS: TabValue[] = ["assyst-drive", "file-link"];

export default function DocumentManagement(props: any) {
  const navigate = useNavigate();
  const { clientId, "*": splatPath } = useParams<{ clientId: string; "*": string }>();

  // First segment of splat is the tab, the rest is the folder path
  const segments = (splatPath ?? "").split("/").filter(Boolean);
  const tabSegment = segments[0];
  const activeTab: TabValue = TABS.includes(tabSegment as TabValue)
    ? (tabSegment as TabValue)
    : "assyst-drive";
  const folderPath = TABS.includes(tabSegment as TabValue)
    ? segments.slice(1).join("/")
    : "";

  const handleTabSelect = (_: any, data: any) => {
    navigate(`/clients/${clientId}/documents/${data.value}`);
  };

  return (
    <div className="doc-mgmt-container">
      <TabList selectedValue={activeTab} onTabSelect={handleTabSelect}>
        <Tab value="assyst-drive">Assyst Drive</Tab>
        <Tab value="file-link">File Link</Tab>
      </TabList>

      <div className="doc-mgmt-tab-content">
        {activeTab === "assyst-drive" && (
          <DrivePanel
            key="assyst-drive"
            activeCustomerId={props.activeCustomerId}
            notify={props.notify}
            clientId={clientId ?? ""}
            splatPath={folderPath}
            tab="assyst-drive"
          />
        )}
        {activeTab === "file-link" && (
          <FileLinkPanel
            activeCustomerId={props.activeCustomerId}
            notify={props.notify}
          />
        )}
      </div>
    </div>
  );
}
